import { defineStore } from 'pinia';
import { ProductMapper } from '@/utils/mappers/ProductMapper';
import {
    EcommerceProduct,
    EcommerceProductApi,
    Option,
    Product,
    ProductApi,
    ProductMeshMaterial,
    UvMapCanvas,
    VariantEcom
} from '@/types/Product';
import type { ProductKind } from '@/product-types/contract';
import { CanvasTexture } from 'three';
import { GraduationMapper } from '@/utils/mappers/GraduationMapper';
import { stripTierSuffix } from '@/utils/variantRef';
import { resolveVariantFor } from '@/utils/variantResolution';
import { Color, Material, HexColor } from '@/types/Material';
import { EcommerceProductMapper } from '@/utils/mappers/EcommerceProductMapper';
import { PriceDetail } from '@/types/Price';
import { parsePriceNumber, formatPriceLike } from '@/utils/priceFormat';
import { addSurcharge } from '@/utils/zonePricing';
import { useZonePricingStore } from '@/stores/zonePricing';
import { findEcommerceColorOption } from '@/utils/ecommerceOptions';
import { PrintInk, PrintProfile } from '@/types/PrintProfile';
import { PrintProfileMapper } from '@/utils/mappers/PrintProfileMapper';

/** Couleur normalisee pour comparer deux materiaux : minuscules, #abc -> #aabbcc. */
function normalizeMaterialColor(value?: string | null): string {
    const raw = (value ?? '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    const hex = raw.startsWith('#') ? raw.slice(1) : raw;
    if (/^[0-9a-f]{3}$/.test(hex)) {
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
    return /^[0-9a-f]{6}$/.test(hex) ? `#${hex}` : raw;
}

/**
 * Index du materiau de `materials` correspondant a la couleur choisie sur le mesh
 * de reference. On tente, dans l'ordre : le meme nom de couleur, la meme valeur
 * hexa, puis la meme POSITION dans la liste (les palettes des meshes d'un meme
 * modele sont ordonnees pareil). -1 si le mesh n'a vraiment aucun equivalent.
 */
function findMatchingMaterialIndex(
    materials: ProductMeshMaterial[],
    reference: ProductMeshMaterial,
    referencePosition: number
): number {
    const targetName = (reference.colorName ?? '').trim().toLowerCase();
    if (targetName) {
        const byName = materials.findIndex(
            (material) => (material.colorName ?? '').trim().toLowerCase() === targetName
        );
        if (byName !== -1) {
            return byName;
        }
    }
    const targetColor = normalizeMaterialColor(reference.color);
    if (targetColor) {
        const byColor = materials.findIndex((material) => normalizeMaterialColor(material.color) === targetColor);
        if (byColor !== -1) {
            return byColor;
        }
    }
    return referencePosition >= 0 && referencePosition < materials.length ? referencePosition : -1;
}

export const useProductStore = defineStore('product', {
    state: () => ({
        product: {} as Product,
        canvasTexture: null as CanvasTexture | null,
        _prices: null as PriceDetail | null,
        selectedMaterial: null as Material | null,
        selectedColor: null as Color | HexColor | null,
        selectedMaterialIndices: [] as number[],
        graduations: [] as Graduation[],
        screenshots: [] as string[],
        screenshotZones: [] as Array<{ label: string; screenshots: string[] }>,
        ecommerceProduct: {
            options: [],
            variants: []
        } as EcommerceProduct,
        /**
         * Identité du produit e-commerce courant (handle Medusa ou id produit), pour
         * détecter un CHANGEMENT de produit et réinitialiser `selectedOptions`. Sans
         * ça, des valeurs d'option de l'ancien produit survivaient et rendaient
         * `selectedVariant` introuvable → `addToCart` envoyait `variantRef: null`.
         */
        ecommerceProductHandle: null as string | null,
        /**
         * ID du produit C3D (3D) auquel le contexte e-commerce courant appartient.
         * Renseigné UNIQUEMENT depuis la donnée `/detailed` (qui porte variants + le
         * produit). Si le produit chargé change vers un produit SANS mapping
         * e-commerce (backend renvoie `variants: null` → processEcommerceData
         * early-return), cet id reste sur l'ANCIEN produit → on détecte le décalage
         * et on refuse d'envoyer un variantRef périmé au panier (sinon MAUVAIS produit
         * commandé). Voir getter `ecommerceContextIsStale`.
         */
        ecommerceOwnerProductId: null as string | null,
        /**
         * RC3 — base du variantRef du DERNIER `askEcommerceProduct` émis (produit
         * courant attendu de l'hôte). Sert à IGNORER une réponse `sendEcommerceProduct`
         * tardive pour un ANCIEN produit (changement rapide de produit) qui écraserait
         * sinon le contexte du produit courant. `null` = pas d'attente (ex. préchargement
         * initial de l'hôte) → on accepte.
         */
        pendingEcommerceHandle: null as string | null,
        /**
         * Base (variantRef sans suffixe `_qty`) du MODÈLE e-commerce sélectionné.
         * Indispensable pour les produits « thème » : un même produit Medusa expose
         * PLUSIEURS modèles (gobelet, verre, flûte…) en variantes qui partagent les
         * MÊMES options (Quantity). Identifier la variante par les seules options
         * renverrait alors le 1er modèle de la liste (mauvais prix/produit au panier).
         * On mémorise donc la base choisie pour discriminer. `null` = pas de thème.
         */
        selectedBaseVariantRef: null as string | null,
        selectedOptions: {} as Record<string, string>,
        /**
         * MODE MULTI-VARIANTES (v2) — coloris retenus pour la commande, dans
         * l'ordre de sélection (= ordre des lignes de panier). Le coloris
         * PRÉVISUALISÉ reste porté par `selectedOptions` / `selectedMaterialIndices`
         * comme en v1 : ce tableau dit « ce qui part au panier », pas « ce qu'on
         * regarde ».
         *
         * Vide en v1, et RIEN dans le code historique ne le lit : c'est l'adapter
         * `multi` qui le tient. Cf. `variant-modes/`.
         */
        selectedVariantColorNames: [] as string[],
        /**
         * MODE MULTI-VARIANTES (v2) — quantité commandée par coloris. Le PALIER de
         * prix, lui, se calcule sur la SOMME (frais de calage mutualisés) :
         * cf. utils/variantTierPricing.ts.
         */
        quantityByColorName: {} as Record<string, number>,
        /**
         * Quantité SAISIE LIBREMENT (mode quantityMode = tiersAndFree | free), ou
         * quantité hors palier reçue de l'hôte. `null` = mode paliers classique.
         * Le PRIX suit le palier ≤ freeQuantity (selectedOptions.Quantity aligné,
         * comme le VDP) ; la quantité RÉELLE facturée = freeQuantity.
         */
        freeQuantity: null as number | null,
        /**
         * Methode d'impression selectionnee GLOBALEMENT pour le produit : UNE seule
         * methode pour toutes les zones (pas de distinction par zone pour l'instant).
         * null => profil digital implicite (comportement historique 100% inchange).
         */
        selectedPrintProfile: null as PrintProfile | null,
        /**
         * Encres choisies (hex) GLOBALES pour la serigraphie (slots "Encre 1..N",
         * N = profile.colorCount). Palette UNIQUE appliquee a tout le design, toutes
         * zones confondues : chaque couleur est snappee vers l'encre la plus proche
         * de cette palette. Vide pour digital/laser.
         */
        selectedPrintInks: [] as string[],
        /**
         * Couleurs PRÉ-SÉLECTIONNÉES par l'hôte (storefront) via PostMessage
         * (`setInkColors`). Hex bruts : l'assistant d'encres les mappe sur la palette
         * la plus proche et les pré-remplit ; la détection auto continue en parallèle.
         */
        presetInkColors: [] as string[],
        /**
         * VDP demandée par l'HÔTE (storefront) : postMessage
         * `sendEcommerceProduct.vdpEnabled` ou param URL `?vdp=1`. Distinct du
         * `vdpEnabled` du produit C3D (réglage BO) : les deux sont des sources
         * indépendantes, l'une OU l'autre suffit. Cf. isVdpRequested.
         */
        hostVdpEnabled: false,
        /**
         * Contexte « pourline certifiée » demandé par l'HÔTE : postMessage
         * `sendEcommerceProduct.certifiedPourline` (ou `CertifiedPourline`).
         * Quand true, en mode graduation `auto` on ne propose que les graduations
         * certifiées et on pré-applique le défaut certifié. Cf. isCertifiedPourlineRequested.
         */
        hostCertifiedPourline: false,
        /**
         * Mockups demandés par l'HÔTE (storefront) : postMessage
         * `sendEcommerceProduct.mockupEnabled`, message `{ eventType: 'mockup' }`
         * ou param URL `?mockup=1|0`.
         *
         * TROIS états, et la nuance compte : `null` = l'hôte ne s'est pas
         * prononcé, on suit alors le réglage du site. `false` n'est donc PAS la
         * valeur par défaut, c'est un refus explicite. Cf. isMockupRequested.
         */
        hostMockupEnabled: null as boolean | null,
        /**
         * MODE DE COMMANDE demandé par l'HÔTE (storefront) : champ
         * `sendEcommerceProduct.variantMode`, ou message `{ eventType:'variantMode' }`.
         *
         * `null` = l'hôte ne s'est pas prononcé, on suit alors le réglage du site.
         * `'single'` n'est donc PAS la valeur par défaut, c'est un choix explicite —
         * une boutique doit pouvoir forcer le mono-coloris page par page sans
         * toucher à la configuration du site. Cf. variant-modes/registry.
         */
        hostVariantMode: null as 'single' | 'multi' | null,
        /**
         * Méthode verrouillée : true quand la méthode est imposée en amont (param
         * URL `print_profile_id` en e-commerce, ou étape de choix au catalogue).
         * L'utilisateur ne peut plus changer de méthode pendant la personnalisation
         * (le sélecteur de la barre d'outils est masqué). false => legacy.
         */
        printMethodLocked: false,
        /**
         * Broderie : l'utilisateur peut DÉSACTIVER l'effet de rendu broderie (tatami)
         * dans l'aperçu, via un toggle (colonne droite / Configuration). Runtime only,
         * n'affecte que l'aperçu (la sortie machine reste côté ExportService). Le
         * toggle n'est proposé que si la méthode est broderie ET que l'effet est
         * activé au BO (PrintProfile.embroideryRenderEnabled). Cf. useEmbroideryEffect.
         */
        embroideryEffectDisabled: false
    }),
    actions: {
        setProduct(productData: ProductApi) {
            const ecommerceColorOption = findEcommerceColorOption(this.ecommerceProduct.options);
            const chosenColorName = ecommerceColorOption ? this.selectedOptions[ecommerceColorOption.name] : undefined;
            const mappedProduct = ProductMapper.getProductFromData(productData, chosenColorName);
            this.product = mappedProduct;
            this.syncSelectedColorFromOptions();
            this.applyEcommerceColorSelectionToMaterials();
            // Nouveau produit : on repart de la methode digitale implicite (aucune
            // contrainte). Reset de la selection globale pour ne pas trainer un
            // profil serigraphie d'un produit precedent.
            this.selectedPrintProfile = null;
            this.selectedPrintInks = [];
            // Nouveau produit : l'effet broderie repart activé (le toggle utilisateur
            // ne doit pas fuir d'un produit à l'autre).
            this.embroideryEffectDisabled = false;
        },

        /**
         * Publie les surfaces éditables du produit.
         *
         * En 3D elles viennent du BO avec l'atlas UV et sont donc déjà là au
         * `setProduct`. En 2D, elles ne sont connues qu'après le chargement des
         * bundles de faces : l'adapter de type les pose ici, et `CanvasNew`
         * construit ses canvases de zone en réaction.
         *
         * `uvMapDataList` est tenu à jour en parallèle : c'est le repli lu par
         * le dimensionnement du canvas principal et par l'aire de mise en page
         * des thèmes.
         */
        setSurfaces(surfaces: UvMapCanvas[]) {
            this.product.uvMapCanvasList = surfaces;
            this.product.uvMapDataList = surfaces.map((surface) => ({
                top: surface.top ?? 0,
                left: surface.left ?? 0,
                width: surface.width ?? 0,
                height: surface.height ?? 0
            }));
        },
        syncSelectedColorFromOptions() {
            const colorList = this.product?.material?.colorList;
            if (!Array.isArray(colorList) || colorList.length === 0) {
                return;
            }

            const ecommerceColorOption = findEcommerceColorOption(this.ecommerceProduct.options);
            if (ecommerceColorOption) {
                const selectedColorName = this.selectedOptions[ecommerceColorOption.name];
                if (selectedColorName) {
                    const candidates = ecommerceColorOption.value?.length
                        ? colorList.filter((color) => ecommerceColorOption.value.includes(color.name || ''))
                        : colorList;
                    const selectedColor = candidates.find((color) => color.name === selectedColorName);
                    if (selectedColor) {
                        this.selectedColor = ProductMapper.convertColorToHexa(selectedColor);
                        return;
                    }
                }
            }

            const defaultColor = this.product.material.colorsFor3DObject?.[0];
            if (defaultColor) {
                this.selectedColor = defaultColor;
            }
        },
        setCanvasTexture(newCanvasTexture: CanvasTexture) {
            this.canvasTexture = newCanvasTexture;
        },
        setGraduations(graduations: GraduationApi[]) {
            const graduationList: Graduation[] = [];
            graduations.forEach((graduation: GraduationApi) => {
                graduationList.push(GraduationMapper.getGraduationFromData(graduation));
            });

            this.graduations = graduationList || [];
        },
        setScreenshots(screenshots: string[]) {
            this.screenshots = screenshots;
        },
        setEcommerceProduct(ecommerceProductApi: EcommerceProductApi) {
            if (!ecommerceProductApi) {
                return;
            }
            const ecommerceProduct = EcommerceProductMapper.getEcommerceProductFromData(ecommerceProductApi);
            this.ecommerceProduct = ecommerceProduct;
            // Changement de produit DANS le configurateur (sans repasser par l'hôte) :
            // `selectedOptions` est mergé (jamais remplacé) par updateSelectedOptions.
            // Des VALEURS d'option de l'ANCIEN produit survivaient sur des clés partagées
            // (ex. « Couleur=Noir » alors que le nouveau produit n'a pas « Noir », ou
            // « Matiere » absente du selectedVariant du nouveau produit). `selectedVariant`
            // exige que CHAQUE option sélectionnée matche une variante → aucune ne matchait
            // → `addToCart` envoyait `variantRef: null` et le storefront retombait sur le
            // produit de l'URL initiale (mauvais produit au panier, fichiers pourtant bons).
            // Correctif : on RÉINITIALISE `selectedOptions` quand le PRODUIT e-commerce
            // change (identité = handle Medusa, sinon id produit). Sur une simple
            // re-synchro du MÊME produit (l'hôte renvoie `sendEcommerceProduct` 2×),
            // l'identité est stable → on préserve les choix en cours de l'utilisateur.
            const newHandle =
                (ecommerceProductApi as { productHandle?: string }).productHandle ??
                ecommerceProductApi.product?.id ??
                null;
            if (newHandle != null && newHandle !== this.ecommerceProductHandle) {
                this.selectedOptions = {};
                this.freeQuantity = null;
                this.selectedBaseVariantRef = null;
                // Idem pour la sélection multi-variantes : des coloris de l'ANCIEN
                // produit survivraient sinon et produiraient des lignes de panier
                // pointant sur des variantes qui n'existent plus ici.
                this.selectedVariantColorNames = [];
                this.quantityByColorName = {};
            }
            if (newHandle != null) {
                this.ecommerceProductHandle = newHandle;
            }
            // VDP demandée par l'hôte. On ne remet JAMAIS le flag à false ici :
            // le param URL `?vdp=1` est posé avant l'arrivée du postMessage, et
            // un hôte plus ancien (payload sans `vdpEnabled`) l'écraserait.
            if (ecommerceProductApi.vdpEnabled === true) {
                this.hostVdpEnabled = true;
            }
            // Contexte pourline certifiée demandé par l'hôte (idem : on ne remet
            // jamais à false, un hôte ancien sans le champ ne doit pas l'écraser).
            if (
                ecommerceProductApi.certifiedPourline === true ||
                (ecommerceProductApi as any).CertifiedPourline === true
            ) {
                this.hostCertifiedPourline = true;
            }
            // Mockups : contrairement à la VDP, on accepte un refus EXPLICITE
            // (`false`) — une boutique doit pouvoir les couper page par page. Un
            // hôte plus ancien n'envoie pas le champ : `undefined` ne touche à rien.
            if (typeof ecommerceProductApi.mockupEnabled === 'boolean') {
                this.hostMockupEnabled = ecommerceProductApi.mockupEnabled;
            }
            // Mode de commande : comme les mockups, on accepte les DEUX sens — un
            // mode est un choix, pas une capacité qu'on n'activerait qu'une fois.
            // Valeur inconnue ou absente => on ne touche à rien.
            this.setHostVariantMode(ecommerceProductApi.variantMode);
        },
        /**
         * Marque le produit C3D propriétaire du contexte e-commerce courant. Appelé
         * depuis processEcommerceData quand une donnée `/detailed` avec variantes est
         * chargée (elle porte `product.id`). Jamais depuis la donnée hôte (qui n'a pas
         * de `product`), pour ne pas fausser la comparaison en flux `/variants`.
         */
        setEcommerceOwner(productId: string | null) {
            if (productId) {
                this.ecommerceOwnerProductId = productId;
            }
        },
        /**
         * Mode de commande demandé par l'hôte. Ignore toute valeur inconnue plutôt
         * que de retomber sur un défaut : un hôte plus ancien n'envoie rien, et ne
         * doit surtout pas écraser le réglage du site.
         */
        setHostVariantMode(mode: unknown) {
            const normalized = typeof mode === 'string' ? mode.trim().toLowerCase() : '';
            if (normalized === 'single' || normalized === 'multi') {
                this.hostVariantMode = normalized;
            }
        },
        /** RC3 : mémorise le produit e-commerce attendu (base du ref demandé à l'hôte). */
        setPendingEcommerceHandle(ref: string | null) {
            this.pendingEcommerceHandle = ref ? stripTierSuffix(ref) : null;
        },
        /** Mémorise la base (modèle) du variant sélectionné (produit « thème »). */
        setSelectedBaseVariantRef(base: string | null) {
            this.selectedBaseVariantRef = base;
        },
        /**
         * RC3 : une réponse `sendEcommerceProduct` correspond-elle au produit e-commerce
         * ATTENDU ? Vrai si aucune attente (préchargement initial) OU si l'une des
         * variantes du payload a la même base que `pendingEcommerceHandle`. Sinon la
         * réponse est périmée (ancien produit) et doit être ignorée.
         */
        ecommercePayloadMatchesPending(variantRefs: string[]): boolean {
            if (!this.pendingEcommerceHandle) {
                return true;
            }
            const base = this.pendingEcommerceHandle;
            return variantRefs.some((r) => stripTierSuffix(r) === base);
        },
        updateSelectedOptions(options: Record<string, string>) {
            if (!this.ecommerceProduct.options || !this.ecommerceProduct.variants) {
                console.error('Missing options or variants in ecommerce product');
                return;
            }
            this.selectedOptions = { ...this.selectedOptions, ...options };

            // Produit pas encore chargé : setProduct rappellera sync + materials
            if (!this.product?.material?.colorList?.length) {
                return;
            }

            this.syncSelectedColorFromOptions();
            this.applyEcommerceColorSelectionToMaterials();
        },
        /**
         * Sélection d'un PALIER dans le dropdown (mode paliers) : on efface toute
         * quantité libre et on pose la valeur du palier telle quelle.
         */
        setTierQuantity(value: string) {
            this.freeQuantity = null;
            const opt = this.ecommerceDisplayedOption;
            if (opt) {
                this.updateSelectedOptions({ [opt.name]: value });
            }
        },
        /**
         * Saisie LIBRE d'une quantité : quantité mini imposée = plus petit palier.
         * On mémorise la vraie quantité (freeQuantity) et on aligne le prix sur le
         * palier ≤ quantité (selectedOptions.Quantity), exactement comme le VDP.
         */
        setFreeQuantity(q: number | null) {
            if (q == null || !Number.isFinite(q)) {
                this.freeQuantity = null;
                return;
            }
            const min = this.minTier ?? 1;
            const clamped = Math.max(min, Math.floor(q));
            this.freeQuantity = clamped;
            const opt = this.ecommerceDisplayedOption;
            const tierVal = this.resolveTierValueFor(clamped);
            if (opt && tierVal !== undefined) {
                this.updateSelectedOptions({ [opt.name]: tierVal });
            }
        },
        applyEcommerceColorSelectionToMaterials() {
            const ecommerceColorOption = findEcommerceColorOption(this.ecommerceProduct.options);
            if (!ecommerceColorOption) {
                return;
            }

            const selectedColorName = this.selectedOptions[ecommerceColorOption.name];
            if (!selectedColorName || !Array.isArray(this.product.meshes) || this.product.meshes.length === 0) {
                return;
            }

            const nextIndices =
                this.selectedMaterialIndices.length === this.product.meshes.length
                    ? [...this.selectedMaterialIndices]
                    : Array.from({ length: this.product.meshes.length }, () => 0);

            console.log('[DEBUG applyEcommerceColor] selectedColorName (ecommerce):', selectedColorName);
            let hasChanged = false;
            this.product.meshes.forEach((mesh, meshIndex) => {
                const materialIndex =
                    mesh.materials?.findIndex((material) => material.colorName === selectedColorName) ?? -1;
                console.log(
                    `[DEBUG applyEcommerceColor] mesh[${meshIndex}] "${mesh.name}" — colorNames dispo:`,
                    mesh.materials?.map((m) => m.colorName),
                    '→ index trouvé:',
                    materialIndex
                );
                if (materialIndex >= 0 && nextIndices[meshIndex] !== materialIndex) {
                    nextIndices[meshIndex] = materialIndex;
                    hasChanged = true;
                } else if (materialIndex < 0) {
                    const allowed = this.availableMaterialsByMesh[meshIndex] ?? [];
                    if (
                        allowed.length > 0 &&
                        !allowed.some((entry) => entry.materialIndex === nextIndices[meshIndex])
                    ) {
                        nextIndices[meshIndex] = allowed[0].materialIndex;
                        hasChanged = true;
                    }
                }
            });
            console.log('[DEBUG applyEcommerceColor] selectedMaterialIndices résultants:', nextIndices);

            if (hasChanged || this.selectedMaterialIndices.length !== nextIndices.length) {
                this.selectedMaterialIndices = nextIndices;
            }
        },
        setSelectedColor(color: Color) {
            if (!color) {
                return;
            }
            const ecommerceColorOption = findEcommerceColorOption(this.ecommerceProduct.options);
            if (ecommerceColorOption) {
                this.selectedOptions[ecommerceColorOption.name] = color.name as string;
            }
            this.selectedColor = ProductMapper.convertColorToHexa(color);
        },
        initSelectedMaterialIndices(meshCount: number) {
            this.selectedMaterialIndices = Array.from({ length: meshCount }, () => 0);
            this.applyEcommerceColorSelectionToMaterials();
        },
        setSelectedMaterialIndex(meshIndex: number, materialIndex: number) {
            if (meshIndex < 0 || meshIndex >= this.selectedMaterialIndices.length) {
                return;
            }
            this.selectedMaterialIndices[meshIndex] = materialIndex;
            this.selectedMaterialIndices = [...this.selectedMaterialIndices];

            const selectedMesh = this.product.meshes?.[meshIndex];
            const selectedMaterial = selectedMesh?.materials?.[materialIndex];
            const selectedColor = selectedMaterial?.colorName;

            const ecommerceColorOption = findEcommerceColorOption(this.ecommerceProduct.options);

            if (ecommerceColorOption) {
                this.selectedOptions[ecommerceColorOption.name] = selectedColor;
            }
        },
        /**
         * Applique un materiau choisi dans la liste UNIQUE de couleurs produit.
         *
         * Le mesh de REFERENCE (celui qui porte les zones, cf.
         * `primaryMaterialMeshIndex`) prend l'index demande, puis TOUS les autres
         * meshes du modele passent a la meme couleur — par nom, a defaut par valeur
         * hexa, a defaut par position dans leur palette (cf.
         * `findMatchingMaterialIndex`). Un modele decoupe en plusieurs meshes
         * (ex. casquette : Sphere005_1..5) change ainsi de couleur d'un seul bloc,
         * sans exposer une liste de couleurs par mesh.
         */
        setSelectedProductMaterial(materialIndex: number) {
            const referenceMeshIndex = this.primaryMaterialMeshIndex;
            const referenceMaterial = this.product.meshes?.[referenceMeshIndex]?.materials?.[materialIndex];
            this.setSelectedMaterialIndex(referenceMeshIndex, materialIndex);
            if (!referenceMaterial) {
                return;
            }

            const nextIndices = [...this.selectedMaterialIndices];
            let hasChanged = false;
            (this.product.meshes ?? []).forEach((mesh, meshIndex) => {
                if (meshIndex === referenceMeshIndex || meshIndex >= nextIndices.length) {
                    return;
                }
                const twinIndex = findMatchingMaterialIndex(mesh.materials ?? [], referenceMaterial, materialIndex);
                if (twinIndex >= 0 && nextIndices[meshIndex] !== twinIndex) {
                    nextIndices[meshIndex] = twinIndex;
                    hasChanged = true;
                }
            });
            if (hasChanged) {
                this.selectedMaterialIndices = nextIndices;
            }
        },
        /**
         * Selectionne la methode d'impression (profil) D'UNE ZONE. Reinitialise
         * les encres choisies de cette zone pour un profil serigraphie (par defaut
         * les N premieres encres de la palette) et demande une (re)application des
         * contraintes au moteur d'edition via un event ecoute par
         * usePrintQuantization.
         */
        /**
         * Selectionne la methode d'impression (profil) GLOBALE du produit.
         * Reinitialise les encres pour un profil serigraphie (les N premieres encres
         * de la palette) et redemande une (re)application des contraintes au moteur
         * d'edition via un event ecoute par usePrintQuantization.
         */
        setPrintProfile(profile: PrintProfile) {
            if (!profile) {
                return;
            }
            const previous = this.selectedPrintProfile?.method ?? null;
            this.selectedPrintProfile = profile;
            // Propriété commune : toutes les métriques suivantes sont ainsi
            // segmentables par méthode (numérique / sérigraphie / broderie…).
            registerAnalyticsContext({ print_method: profile.method ?? null });
            if (previous && previous !== profile.method) {
                trackEvent('print_method_changed', { from: previous, to: profile.method ?? null });
            }

            if (
                (profile.method === 'screen' || profile.method === 'embroidery') &&
                profile.palette &&
                profile.colorCount
            ) {
                const inks = profile.palette.inks;
                const count = Math.min(profile.colorCount, inks.length);
                // Pre-remplit les slots avec les premieres encres/fils de la palette.
                this.selectedPrintInks = inks.slice(0, count).map((ink) => ink.hex);
            } else {
                this.selectedPrintInks = [];
            }

            this.requestPrintConstraintsReapply();
        },
        /**
         * Verrouille la méthode sur un profil identifié par son id (param URL
         * `print_profile_id` en e-commerce, ou choix au catalogue). Cherche le
         * profil parmi ceux proposés par le produit ; si trouvé, l'applique et
         * marque la méthode comme verrouillée. Retourne true si verrouillé.
         * Introuvable (donnée obsolète, non mappé) => no-op, comportement legacy.
         */
        lockPrintProfileById(profileId: string | null | undefined): boolean {
            if (!profileId) {
                return false;
            }
            const profile =
                this.availablePrintProfiles.find((p) => p.id === profileId) ??
                (this.product?.printProfiles ?? []).find((p) => p.id === profileId) ??
                null;
            if (!profile) {
                return false;
            }
            this.setPrintProfile(profile);
            this.printMethodLocked = true;
            return true;
        },
        /** Assigne l'encre d'un slot (serigraphie) et redemande la quantification. */
        setPrintInk(slotIndex: number, hex: string) {
            if (slotIndex < 0) {
                return;
            }
            const next = [...this.selectedPrintInks];
            while (next.length <= slotIndex) {
                next.push('');
            }
            next[slotIndex] = hex;
            this.selectedPrintInks = next;
            this.requestPrintConstraintsReapply();
        },
        /** Remplace l'ensemble des encres choisies et redemande la quantification. */
        setPrintInks(inks: string[]) {
            this.selectedPrintInks = Array.isArray(inks) ? [...inks] : [];
            this.requestPrintConstraintsReapply();
        },
        /** Couleurs pré-sélectionnées transmises par l'hôte (PostMessage setInkColors). */
        setPresetInkColors(colors: string[]) {
            this.presetInkColors = Array.isArray(colors)
                ? colors
                      .filter((c) => typeof c === 'string' && /^#?[0-9a-fA-F]{6}$/.test(c.trim()))
                      .map((c) => (c.trim().startsWith('#') ? c.trim() : `#${c.trim()}`))
                : [];
        },
        // --- Compat : anciens setters "par zone". La methode etant desormais GLOBALE,
        // le zoneId est ignore et delegue au modele global. Conserves pour ne pas
        // toucher les appelants existants (pickers).
        setZoneProfile(_zoneId: string, profile: PrintProfile) {
            this.setPrintProfile(profile);
        },
        setZoneInk(_zoneId: string, slotIndex: number, hex: string) {
            this.setPrintInk(slotIndex, hex);
        },
        setZoneInks(_zoneId: string, inks: string[]) {
            this.setPrintInks(inks);
        },
        /**
         * Signale au moteur d'edition (usePrintQuantization, monte au niveau du
         * canvas) qu'il doit re-appliquer les contraintes de la methode courante.
         * On passe par un CustomEvent pour eviter un import croise store->canvas.
         */
        requestPrintConstraintsReapply() {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('print-constraints-reapply'));
            }
        },
        /**
         * MODE MULTI-VARIANTES (v2) — remplace la liste des coloris commandés.
         * Passer par une action plutôt que muter l'état depuis l'adapter garde le
         * point d'écriture unique et traçable dans les devtools.
         */
        setSelectedVariantColorNames(colorNames: string[]) {
            this.selectedVariantColorNames = [...colorNames];
            // Purge des quantités orphelines : un coloris retiré ne doit pas
            // continuer à peser dans la somme qui détermine le palier de prix.
            const kept: Record<string, number> = {};
            for (const name of this.selectedVariantColorNames) {
                if (this.quantityByColorName[name] != null) {
                    kept[name] = this.quantityByColorName[name];
                }
            }
            this.quantityByColorName = kept;
        },
        /** MODE MULTI-VARIANTES (v2) — quantité commandée pour un coloris. */
        setVariantQuantity(colorName: string, quantity: number | null) {
            if (!colorName) {
                return;
            }
            if (quantity == null || !Number.isFinite(quantity) || quantity <= 0) {
                const rest = { ...this.quantityByColorName };
                delete rest[colorName];
                this.quantityByColorName = rest;
                return;
            }
            this.quantityByColorName = { ...this.quantityByColorName, [colorName]: Math.floor(quantity) };
        },
        updateVariants(variantsData: VariantEcom[]) {
            const variants = this.ecommerceProduct.variants;
            // RC4 : la correspondance stricte par `variantRef` peut échouer quand les
            // refs ne diffèrent que par le suffixe de palier `_qty` (base nue vs
            // éclatée, variante Medusa recréée). On tolère alors une correspondance par
            // BASE (ref sans `_<qty>`) — MAIS uniquement si elle est NON AMBIGUË (une
            // seule variante partage cette base), pour ne jamais appliquer le prix d'un
            // palier au mauvais palier. Sinon on log clairement (plus de skip muet).
            const stripQty = (r: string | undefined | null): string => stripTierSuffix(r);
            variantsData.forEach((variantData: VariantEcom) => {
                let existingVariant = variants.find((variant) => variant.variantRef == variantData.variantRef);
                if (!existingVariant) {
                    const base = stripQty(variantData.variantRef);
                    const candidates = variants.filter((v) => stripQty(v.variantRef) === base);
                    if (candidates.length === 1) {
                        existingVariant = candidates[0];
                    }
                }
                if (!existingVariant) {
                    console.error(
                        `[updateVariants] variantRef "${variantData.variantRef}" introuvable — prix non appliqué. ` +
                            `Refs connues: ${variants
                                .slice(0, 6)
                                .map((v) => v.variantRef)
                                .join(', ')}${variants.length > 6 ? '…' : ''}`
                    );
                    return;
                }
                const variantIndex = variants.indexOf(existingVariant);
                const updatedVariant = EcommerceProductMapper.updateVariantFromEcomData(existingVariant, variantData);
                this.ecommerceProduct.variants[variantIndex] = updatedVariant;
            });
        }
    },
    getters: {
        /**
         * Session E-COMMERCE : vrai UNIQUEMENT si un produit e-commerce a été reçu
         * par POSTMESSAGE (`setEcommerceProduct`, appelé seulement quand variants +
         * options sont présents, cf. useProduct.processEcommerceData). Faux en
         * catalogue / URL directe. Sert à n'activer certaines étapes (ex. VDP en
         * parcours simplifié) QUE lorsque le storefront les a demandées.
         */
        /**
         * Type de support du produit. Exposé pour les rares consommateurs qui ont
         * besoin de l'information brute (journalisation, télémétrie) ; le
         * comportement, lui, passe par l'adapter de type — cf. `product-types/`.
         */
        productKind: (state): ProductKind => state.product?.kind ?? '3d',
        /**
         * Produit sur support plat (2D). NE PAS confondre avec
         * `stageStore.configuration.view2d`, qui est la vue « à plat » d'un
         * produit 3D — deux notions orthogonales.
         */
        isFlatProduct: (state): boolean => (state.product?.kind ?? '3d') === '2d',
        hasEcommerceContext: (state): boolean => (state.ecommerceProduct?.variants?.length ?? 0) > 0,
        /**
         * VDP demandée pour ce produit, quelle qu'en soit la source : réglage du
         * produit C3D (BO) OU demande de l'hôte e-commerce (postMessage / param
         * URL, cf. hostVdpEnabled). Point d'entrée UNIQUE des consommateurs —
         * ne pas relire `product.vdpEnabled` directement, sinon la demande du
         * storefront est ignorée. Le mode site et le profil d'impression
         * gardent leur mot à dire par-dessus (cf. data/layerTypes).
         */
        isVdpRequested: (state): boolean =>
            Boolean((state.product as { vdpEnabled?: boolean })?.vdpEnabled) || state.hostVdpEnabled,
        /** Contexte pourline certifiée demandé par l'hôte (flag reçu à l'entrée). */
        isCertifiedPourlineRequested: (state): boolean => state.hostCertifiedPourline,
        /**
         * Le PRODUIT sait-il produire des mockups ? Réglage back-office
         * (`Product.mockupEnabled`), indépendant de la demande de l'hôte : il faut
         * les deux (cf. composables/mockupExport.ts).
         */
        isMockupCapable: (state): boolean => Boolean((state.product as { mockupEnabled?: boolean })?.mockupEnabled),
        /**
         * Nom de la couleur choisie, tel que le back-office l'a saisi. C'est LUI
         * qui sélectionne la photo de mise en scène : un t-shirt rouge et un bleu
         * ne sont pas la même image.
         *
         * Source prioritaire : l'option e-commerce, tenue à jour dans les deux
         * sens (choix du storefront ET choix de l'utilisateur, cf.
         * setSelectedColor / setSelectedMaterialIndex). Repli hors contexte
         * e-commerce : le matériau appliqué au mesh de référence. `null` => le
         * serveur utilisera la photo par défaut.
         */
        selectedColorName(state): string | null {
            const options = state.ecommerceProduct?.options;
            const colorOption = options ? findEcommerceColorOption(options) : null;
            const fromOption = colorOption ? state.selectedOptions[colorOption.name] : undefined;
            if (typeof fromOption === 'string' && fromOption.trim()) {
                return fromOption.trim();
            }

            const meshIndex = this.primaryMaterialMeshIndex;
            const materialIndex = state.selectedMaterialIndices[meshIndex] ?? 0;
            const colorName = state.product?.meshes?.[meshIndex]?.materials?.[materialIndex]?.colorName;

            return typeof colorName === 'string' && colorName.trim() ? colorName.trim() : null;
        },
        /**
         * Plafond du nombre de lignes VDP = plus grande QUANTITÉ proposée dans les
         * PALIERS DE PRIX (tier prices) du produit. La quantité imprimée en VDP =
         * nombre de lignes ; on ne peut donc pas dépasser la quantité max commandable.
         *
         * La donnée est DÉJÀ dans le postMessage : chaque variante porte son palier
         * via l'option « Quantity ». On prend le max sur toutes les variantes reçues.
         * Produit SANS palier (prix unique pour 1, aucune option Quantity) => null =>
         * AUCUN plafond (règle ignorée, cf. demande). Hors e-commerce => null.
         */
        vdpMaxRows(): number | null {
            if (!this.hasEcommerceContext) {
                return null;
            }
            const quantityOf = (opts: Record<string, string> | undefined): number => {
                if (!opts) {
                    return NaN;
                }
                for (const k of Object.keys(opts)) {
                    if (k.toLowerCase() === 'quantity') {
                        return Number(opts[k]);
                    }
                }
                return NaN;
            };
            let max = 0;
            for (const v of this.ecommerceProduct.variants || []) {
                const q = quantityOf(v?.options);
                if (Number.isFinite(q) && q > max) {
                    max = q;
                }
            }
            return max > 0 ? Math.floor(max) : null;
        },
        colors: (state) => {
            const ecommerceColorOption = findEcommerceColorOption(state.ecommerceProduct.options);
            const colorList = state.product?.material?.colorList ?? [];
            if (ecommerceColorOption) {
                return colorList.filter((color) => ecommerceColorOption.value.includes(color.name || ''));
            }
            return colorList;
        },
        /**
         * Variante e-commerce du coloris PRÉVISUALISÉ (comportement v1 inchangé).
         *
         * La résolution vit dans `utils/variantResolution.ts` : le mode
         * multi-variantes doit résoudre la variante d'un coloris AUTRE que celui
         * affiché, et deux implémentations de la même règle finiraient par diverger
         * (le prix ici, le panier là). `undefined` et non `null` : le type de
         * retour historique, que lisent `prices`, l'add-to-cart et les mockups.
         */
        selectedVariant: (state) => {
            return (
                resolveVariantFor({
                    variants: state.ecommerceProduct.variants,
                    baseRef: state.selectedBaseVariantRef,
                    options: state.selectedOptions
                }) ?? undefined
            );
        },
        /**
         * Le contexte e-commerce courant appartient-il à un AUTRE produit que le
         * produit 3D chargé ? Vrai quand on a changé vers un produit SANS mapping
         * e-commerce (backend `variants: null`) : `ecommerceProduct`/`selectedVariant`
         * restent sur l'ancien produit. Dans ce cas l'add-to-cart NE DOIT PAS envoyer
         * le variantRef (sinon MAUVAIS produit commandé). `null` owner (flux `/variants`
         * piloté par l'hôte) => jamais périmé.
         */
        ecommerceContextIsStale(state): boolean {
            return (
                state.ecommerceOwnerProductId != null &&
                state.product?.id != null &&
                state.ecommerceOwnerProductId !== state.product.id
            );
        },
        ecommerceColorOption: (state) => {
            return findEcommerceColorOption(state.ecommerceProduct.options);
        },
        /**
         * Methodes d'impression disponibles = [Digital implicite, ...profils
         * backend]. Digital est toujours en tete et toujours present, meme quand
         * le produit n'expose aucun profil (comportement historique).
         */
        availablePrintProfiles(state): PrintProfile[] {
            const backend = state.product?.printProfiles ?? [];
            // Si le backend renvoie deja un profil digital explicite, on ne
            // duplique pas : on garde uniquement l'implicite en tete.
            const nonDigital = backend.filter((p) => p.method !== 'digital');
            return [PrintProfileMapper.createImplicitDigitalProfile(), ...nonDigital];
        },
        /**
         * Liste des zones exposees par le produit (id + label + mesh), source des
         * selections par zone. Vide si le produit n'a pas de zones.
         */
        printZoneList(state): Array<{ id: string; label: string; canvasMeshId: string | number }> {
            const zones: Array<{ id: string; label: string; canvasMeshId: string | number }> = [];
            const canvasList = state.product?.uvMapCanvasList ?? [];
            for (const canvas of canvasList) {
                for (const zone of canvas.zones ?? []) {
                    if (!zone?.id) {
                        continue;
                    }
                    zones.push({ id: zone.id, label: zone.label || zone.id, canvasMeshId: canvas.meshId });
                }
            }
            return zones;
        },
        /**
         * Zone "active" resolue pour les pickers globaux : la zone selectionnee
         * (stageStore.selectedZoneId) si elle est valide, sinon la premiere zone du
         * produit. Permet de scoper la methode/les encres sur une zone concrete
         * meme quand aucune zone n'est explicitement selectionnee.
         */
        resolvedActiveZoneId(): string | null {
            const stageStore = useStageStore();
            const zones = this.printZoneList;
            const selected = stageStore.selectedZoneId;
            if (selected && zones.some((z) => z.id === selected)) {
                return selected;
            }
            return zones[0]?.id ?? null;
        },
        /**
         * Profil d'impression resolu (fallback digital implicite). La methode etant
         * GLOBALE, le zoneId est ignore : toutes les zones partagent le meme profil.
         */
        profileForZone(state) {
            return (_zoneId?: string | null): PrintProfile =>
                state.selectedPrintProfile || PrintProfileMapper.createImplicitDigitalProfile();
        },
        /** Encres (hex) choisies (globales). zoneId ignore. */
        inksForZone(state) {
            return (_zoneId?: string | null): string[] => state.selectedPrintInks || [];
        },
        /** Encres resolues (objets PrintInk) correspondant aux slots choisis (globales). */
        inkObjectsForZone(state) {
            return (_zoneId?: string | null): PrintInk[] => {
                const palette = state.selectedPrintProfile?.palette;
                if (!palette) {
                    return [];
                }
                return (state.selectedPrintInks || [])
                    .map(
                        (hex) =>
                            palette.inks.find((ink) => ink.hex.toLowerCase() === hex.toLowerCase()) || {
                                id: hex,
                                name: hex,
                                hex
                            }
                    )
                    .filter((ink) => !!ink?.hex);
            };
        },
        /** true si la methode globale est non digitale (serigraphie/laser). */
        hasAnyNonDigitalZone(state): boolean {
            return !!state.selectedPrintProfile && state.selectedPrintProfile.method !== 'digital';
        },
        /**
         * Signature stable de l'etat de quantification (global) : profil + encres.
         * Sert de declencheur reactif a usePrintQuantization sans dependre de
         * l'identite des objets profils (recrees par les fallbacks).
         */
        printQuantizationSignature(state): string {
            const p = state.selectedPrintProfile;
            return `${p?.id ?? ''}:${p?.method ?? ''}:${p?.whiteDisabled ? 'nw' : ''}:${(state.selectedPrintInks ?? []).join(',')}`;
        },
        /** Numérique sans blanc actif sur la zone courante (pilote pickers + quantif). */
        activeZoneWhiteDisabled(): boolean {
            const p = this.activeZoneProfile;
            return p?.method === 'digital' && Boolean(p?.whiteDisabled);
        },
        /**
         * Zones + leur selection d'impression resolue, pour le preflight de
         * validation (une carte par zone non digitale).
         */
        zonesWithPrintProfile(): Array<{ id: string; label: string; profile: PrintProfile; inks: string[] }> {
            return this.printZoneList.map((zone) => ({
                id: zone.id,
                label: zone.label,
                profile: this.profileForZone(zone.id),
                inks: this.inksForZone(zone.id)
            }));
        },
        /** Profil d'impression de la zone active resolue (pickers globaux). */
        activeZoneProfile(): PrintProfile {
            return this.profileForZone(this.resolvedActiveZoneId);
        },
        /**
         * Le toggle « Désactiver l'effet broderie » n'est proposé que si la zone
         * active est en broderie ET que l'effet est activé au BO
         * (embroideryRenderEnabled !== false). Sinon rien à désactiver.
         */
        canToggleEmbroideryEffect(): boolean {
            const p = this.activeZoneProfile as { method?: string; embroideryRenderEnabled?: boolean } | undefined;
            return p?.method === 'embroidery' && p?.embroideryRenderEnabled !== false;
        },
        /** Encres (hex) de la zone active resolue. */
        activeZoneInks(): string[] {
            return this.inksForZone(this.resolvedActiveZoneId);
        },
        /** Encres resolues (objets PrintInk) de la zone active resolue. */
        activeZoneInkObjects(): PrintInk[] {
            return this.inkObjectsForZone(this.resolvedActiveZoneId);
        },
        /**
         * Encres/fils choisis serialises { name, pantone, hex } SI la methode est
         * « palette » (serigraphie/broderie), sinon []. Utilise a l'add-to-cart
         * (postMessage selectedInks) ET dans le BAT (tableau sous l'aplat 2D).
         */
        selectedInksPayload(): Array<{ name: string; pantone: string | null; hex: string }> {
            const method = this.activeZoneProfile?.method;
            if (method !== 'screen' && method !== 'embroidery') {
                return [];
            }
            return this.activeZoneInkObjects
                .filter((ink) => !!ink?.hex)
                .map((ink) => ({ name: ink.name || ink.hex, pantone: ink.pantoneRef ?? null, hex: ink.hex }));
        },
        availableMaterialsByMesh(state): Array<Array<{ material: ProductMeshMaterial; materialIndex: number }>> {
            const siteStore = useSiteStore();
            const ecommerceColorOption = findEcommerceColorOption(state.ecommerceProduct.options);
            const allowedColors = ecommerceColorOption?.value ?? [];
            const mustFilter = siteStore.configuration.isIframeMode && allowedColors.length > 0;

            return (state.product?.meshes ?? []).map((mesh) => {
                const all = (mesh.materials ?? []).map((material, materialIndex) => ({ material, materialIndex }));
                if (!mustFilter) {
                    return all;
                }
                const filtered = all.filter(({ material }) => allowedColors.includes(material.colorName));
                return filtered.length > 0 ? filtered : all;
            });
        },
        /**
         * Index, dans `product.meshes`, du mesh de REFERENCE pour le choix de
         * couleur produit. Un modele est souvent decoupe en plusieurs meshes qui
         * proposent la meme palette (ex. casquette : Sphere005_1..5) : on n'expose
         * qu'UNE liste de couleurs, celle du mesh qui porte les zones
         * d'impression (`uvMapCanvasList[].meshId` = nom du mesh, ou son index en
         * format legacy). A defaut, le premier mesh offrant un vrai choix.
         */
        primaryMaterialMeshIndex(state): number {
            const meshes = state.product?.meshes ?? [];
            if (meshes.length <= 1) {
                return 0;
            }
            const lists = this.availableMaterialsByMesh;
            const hasChoice = (meshIndex: number) => (lists[meshIndex]?.length ?? 0) > 1;

            const zoneMeshIndices = (state.product?.uvMapCanvasList ?? [])
                .map((canvas) => {
                    const byName = meshes.findIndex((mesh) => mesh.name === String(canvas.meshId));
                    if (byName !== -1) {
                        return byName;
                    }
                    // Repli ancien format : meshId numerique = index dans product.meshes.
                    const asIndex = Number(canvas.meshId);
                    return Number.isInteger(asIndex) && asIndex >= 0 && asIndex < meshes.length ? asIndex : -1;
                })
                .filter((meshIndex) => meshIndex !== -1);

            const zoneMeshWithChoice = zoneMeshIndices.find(hasChoice);
            if (zoneMeshWithChoice !== undefined) {
                return zoneMeshWithChoice;
            }
            const firstWithChoice = meshes.findIndex((_, meshIndex) => hasChoice(meshIndex));
            if (firstWithChoice !== -1) {
                return firstWithChoice;
            }
            return zoneMeshIndices[0] ?? 0;
        },
        /**
         * Liste UNIQUE de couleurs/matieres produit affichee dans l'UI (celle du
         * mesh de reference, cf. `primaryMaterialMeshIndex`).
         */
        productMaterialList(): Array<{ material: ProductMeshMaterial; materialIndex: number }> {
            return this.availableMaterialsByMesh[this.primaryMaterialMeshIndex] ?? [];
        },
        /** Index du materiau selectionne dans `productMaterialList`. */
        selectedProductMaterialIndex(state): number {
            return state.selectedMaterialIndices[this.primaryMaterialMeshIndex] ?? 0;
        },
        ecommerceDisplayedOption: (state) => {
            return state.ecommerceProduct.options.find((option: Option) => option.type === 'DuringCustomization');
        },
        /** Paliers (quantités) proposés, en NOMBRES triés croissants. */
        priceTierNumbers(): number[] {
            const opt = this.ecommerceDisplayedOption;
            if (!opt?.value?.length) {
                return [];
            }
            return (opt.value as string[])
                .map((v) => parseInt(String(v).match(/\d+/)?.[0] ?? '', 10))
                .filter((n) => Number.isFinite(n))
                .sort((a, b) => a - b);
        },
        /** Plus petit palier (quantité mini imposée en saisie libre). */
        minTier(): number | null {
            return this.priceTierNumbers[0] ?? null;
        },
        /** Plus grand palier (plafond de prix ; le VDP s'y plafonne aussi). */
        maxTier(): number | null {
            const t = this.priceTierNumbers;
            return t.length ? t[t.length - 1] : null;
        },
        /**
         * Pour une quantité Q, la VALEUR d'option du plus grand palier ≤ Q (repli :
         * plus petit palier). Sert à aligner le PRIX sur le palier exact ou inférieur.
         */
        resolveTierValueFor() {
            return (qty: number): string | undefined => {
                const opt = this.ecommerceDisplayedOption;
                if (!opt?.value?.length) {
                    return undefined;
                }
                const pairs = (opt.value as string[])
                    .map((v) => ({ v, n: parseInt(String(v).match(/\d+/)?.[0] ?? '', 10) }))
                    .filter((p) => Number.isFinite(p.n))
                    .sort((a, b) => a.n - b.n);
                if (!pairs.length) {
                    return undefined;
                }
                let chosen = pairs[0];
                for (const p of pairs) {
                    if (p.n <= qty) {
                        chosen = p;
                    }
                }
                return chosen.v;
            };
        },
        /**
         * Quantité RÉELLE (facturée / envoyée à l'hôte) : la saisie libre si présente,
         * sinon le palier sélectionné. Sert au add-to-cart (comme le VDP lineCount).
         */
        effectiveQuantity(): number | null {
            if (this.freeQuantity != null) {
                return this.freeQuantity;
            }
            const opt = this.ecommerceDisplayedOption;
            const raw = opt ? this.selectedOptions[opt.name] : undefined;
            const n = parseInt(String(raw ?? '').match(/\d+/)?.[0] ?? '', 10);
            return Number.isFinite(n) ? n : null;
        },
        /**
         * Détail de prix PAR PALIER pour le dropdown de quantité (cf. capture) : pour
         * chaque palier on retrouve la variante correspondante (mêmes options hors
         * quantité : couleur, matière…), d'où le prix TOTAL. On en dérive le prix
         * unitaire (total ÷ quantité) et l'ÉCONOMIE (% de baisse du prix unitaire vs
         * le plus petit palier = tarif de référence, 0 %).
         */
        tierPricing(): Array<{
            qty: number;
            value: string;
            total: number;
            unit: number;
            savingsPct: number;
            totalLabel: string;
            unitLabel: string;
        }> {
            const opt = this.ecommerceDisplayedOption;
            if (!opt?.value?.length) {
                return [];
            }
            const variants = this.ecommerceProduct.variants || [];
            // Options courantes HORS quantité : ciblent la bonne variante par palier.
            const fixed: Record<string, string> = {};
            for (const [k, v] of Object.entries(this.selectedOptions)) {
                if (k !== opt.name) {
                    fixed[k] = v as string;
                }
            }
            // Produit « thème » : plusieurs modèles partagent les mêmes valeurs de
            // Quantité. Sans discriminer par la BASE (modèle) sélectionnée, `find` par
            // options seul renvoie le palier du 1er modèle de la liste → la modale
            // affichait les prix d'un autre modèle que celui choisi (bug « 8,90€ pour
            // 2 pièces » alors que le vrai total du modèle sélectionné = 17,23€).
            const base = this.selectedBaseVariantRef;
            const stripQty = (r?: string | null): string => stripTierSuffix(r);
            const rows: Array<{ qty: number; value: string; total: number; unit: number; tpl?: string }> = [];
            for (const value of opt.value as string[]) {
                const qty = parseInt(String(value).match(/\d+/)?.[0] ?? '', 10);
                if (!Number.isFinite(qty) || qty <= 0) {
                    continue;
                }
                const variant = variants.find(
                    (vt) =>
                        vt.options?.[opt.name] === value &&
                        Object.entries(fixed).every(([k, val]) => vt.options?.[k] === val) &&
                        (!base || stripQty(vt.variantRef) === base)
                );
                const total = parsePriceNumber(variant?.prices?.currentPrice);
                if (total == null) {
                    continue;
                }
                rows.push({ qty, value, total, unit: total / qty, tpl: variant?.prices?.currentPrice });
            }
            rows.sort((a, b) => a.qty - b.qty);
            if (!rows.length) {
                return [];
            }
            const refUnit = rows[0].unit;
            return rows.map((r) => ({
                qty: r.qty,
                value: r.value,
                total: r.total,
                unit: r.unit,
                savingsPct: refUnit > 0 ? Math.max(0, (1 - r.unit / refUnit) * 100) : 0,
                totalLabel: formatPriceLike(r.tpl, r.total),
                unitLabel: formatPriceLike(r.tpl, r.unit)
            }));
        },
        prices(state): PriceDetail | null {
            // Réutilise la résolution base-aware (getter selectedVariant) : sur un
            // produit « thème », le prix DOIT venir de la variante du modèle choisi,
            // pas du 1er modèle de la liste (bug prix/panier « Gobelet 25cl » alors que
            // « Verre à vin » était sélectionné).
            const matchingVariant = this.selectedVariant;
            const base = matchingVariant ? matchingVariant.prices : state._prices;
            const opt = this.ecommerceDisplayedOption;
            const tierQty = parseInt(
                String((opt ? state.selectedOptions[opt.name] : '') ?? '').match(/\d+/)?.[0] ?? '',
                10
            );
            // Supplément de la combinaison de zones décorées (0 sans prix par zone
            // de l'hôte), UNITAIRE : multiplié par la quantité commandée.
            const surcharge = useZonePricingStore().unitSurcharge;
            const withZoneSurcharge = (price: PriceDetail, quantity: number): PriceDetail =>
                surcharge > 0
                    ? {
                          ...price,
                          currentPrice: addSurcharge(price.currentPrice, surcharge, quantity) ?? price.currentPrice,
                          fullPrice: addSurcharge(price.fullPrice, surcharge, quantity),
                          unitPrice: addSurcharge(price.unitPrice, surcharge)
                      }
                    : price;
            // Saisie libre : le TOTAL réel = quantité saisie × prix UNITAIRE du palier
            // ≤ quantité. `selectedOptions.Quantity` est déjà aligné sur ce palier, donc
            // `base` porte le total du palier ; on le ramène à l'unité puis on multiplie
            // par la quantité réelle. Le reste de `base` (unitPrice, fullPrice) inchangé.
            if (state.freeQuantity != null && base) {
                const tierTotal = parsePriceNumber(base.currentPrice);
                if (Number.isFinite(tierQty) && tierQty > 0 && tierTotal != null) {
                    const freeTotal = (tierTotal / tierQty) * state.freeQuantity;
                    return withZoneSurcharge(
                        { ...base, currentPrice: formatPriceLike(base.currentPrice, freeTotal) },
                        state.freeQuantity
                    );
                }
            }
            return base ? withZoneSurcharge(base, Number.isFinite(tierQty) && tierQty > 0 ? tierQty : 1) : null;
        },
        getMetadata: (state) => {
            return () => {
                return {
                    productId: state.product?.id
                };
            };
        }
    }
});
